// --- Core ---
import {FormApi, setIn} from "final-form";
import {forwardRef, ReactNode} from "react";
import {each} from "underscore";
// --- Material UI ---
import {Divider, MenuItem, Typography} from "@mui/material";
// --- SaniQ ---
import {ColorField} from './forms/fields/ColorField';
import {CountrySelectField} from './forms/fields/CountrySelectField';
import {CountryStateSelectField} from './forms/fields/CountryStateSelectField';
import {FieldCondition} from './forms/fields/FieldCondition';
import {TextField} from './forms/fields/TextField';
import {LocaleField} from './forms/fields/LocaleField';
import {TimeField} from './forms/fields/TimeField';
import {DateTimeField} from './forms/fields/DateTimeField';
import {SwitchField} from './forms/fields/SwitchField';
import {MultiSelectField} from './forms/fields/MultiSelectField';
import {RoleSelectField} from './forms/fields/RoleSelectField';
import {SelectField} from './forms/fields/SelectField';
import {EMailField} from './forms/fields/EMailField';
import {DateRangeField} from './forms/fields/DateRangeField';
import {NumberField} from './forms/fields/NumberField';
import {CheckBoxField, CheckBoxGroupField} from './forms/fields/CheckBoxField';
import {DateField} from './forms/fields/DateField';
import {FieldListener} from './forms/FieldListener';
import {PhoneField} from './forms/fields/PhoneField';
import {RadioField, RadioGroupField} from './forms/fields/RadioField';
import {SliderField} from './forms/fields/SliderField';
import {ToggleButtonGroupField} from './forms/fields/ToggleButtonGroupField';
import {AutocompleteTextField} from './forms/fields/AutocompleteTextField';

interface SelectCategoryItemProps {
  children?: ReactNode;
  color: string;
  title?: string;
}

const SelectCategoryItem = forwardRef<unknown, SelectCategoryItemProps>(({children, color, title}, _) => (
  <>
    <MenuItem disabled style={{opacity: 1}}>
      <Typography variant={"subtitle1"} color={"inherit"} style={{color}}>
        <b>{title || children}</b>
      </Typography>
    </MenuItem>
    <Divider/>
  </>
));
SelectCategoryItem.displayName = "SelectCategoryItem";
/**
 * Clean the form values before building the submission payload.
 *
 * - Null cleared (dirty) values in the form. IMPORTANT: Ignores changes to arrays at the moment.
 *   Without this, optional fields would be transmitted as undefined and remain the original value.
 */
const cleanFormValues = <T extends object>(values: T, form: FormApi<T>) => {
  let result = values;

  each(form.getState().dirtyFields, (dirty, fieldName) => {
    if (fieldName.includes("[")) {
      return;
    }
    const fieldState = form.getFieldState(fieldName as keyof T);

    if (dirty && fieldState?.value === undefined) {
      //NOTE(Keune): setIn returns a new object and does not change the original. This allows the support of nested fields
      result = setIn(result, fieldName, null) as T;
    }
  });

  return result;
};

export {
  ColorField,
  CountrySelectField,
  CountryStateSelectField,
  FieldCondition,
  FieldListener,
  MultiSelectField,
  RoleSelectField,
  SelectCategoryItem,
  SelectField,
  TextField,
  EMailField,
  LocaleField,
  cleanFormValues,
  NumberField,
  PhoneField,
  AutocompleteTextField,
  DateField,
  DateRangeField,
  DateTimeField,
  TimeField,
  SwitchField,
  CheckBoxField,
  CheckBoxGroupField,
  RadioField,
  RadioGroupField,
  SliderField,
  ToggleButtonGroupField
};
